import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';

export type PDFDisplayProps = {
  pdfUrl: string | undefined;
};

export const PDFDisplay: React.FC<PDFDisplayProps> = ({ pdfUrl }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [, setHeight] = useState<number>(0);

  useEffect(() => {
    if (containerRef.current) {
      setHeight(containerRef.current.clientHeight);
    }

    const updateHeight = () => {
      if (containerRef.current) {
        setHeight(containerRef.current.clientHeight);
      }
    };

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  if (!pdfUrl) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.900',
          minHeight: 500,
          gap: 2,
        }}
      >
        <CircularProgress color='success' size={48} />
        <Typography variant='body2' sx={{ color: 'grey.400', fontWeight: 500 }}>
          Loading document...
        </Typography>
      </Box>
    );
  }

  const isDoc = !!pdfUrl?.match(/\.(docx|doc)($|\?|&)/i);
  const fullUrl = pdfUrl?.startsWith('http') || pdfUrl?.startsWith('blob:')
    ? pdfUrl
    : `${window.location.origin}${pdfUrl}`;

  return (
    <Box ref={containerRef} sx={{ bgcolor: 'common.black', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isDoc ? (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1, bgcolor: 'background.paper', position: 'relative' }}>
            <Box
              component='iframe'
              src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
                fullUrl || '',
              )}`}
              width='100%'
              height='100%'
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title='Document Viewer'
            >
              <Typography>Your browser does not support iframes.</Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box
            component='iframe'
            src={`${fullUrl}#toolbar=0`}
            width='100%'
            height='100%'
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title='PDF Viewer'
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4, color: 'common.white', textAlign: 'center' }}>
              <Box>
                <Typography variant='h6' sx={{ mb: 2 }}>
                  Your browser does not support PDF viewing.
                </Typography>
                <Button
                  variant='contained'
                  color='success'
                  href={pdfUrl}
                  target='_blank'
                  rel='noreferrer'
                >
                  Download PDF to View
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PDFDisplay;
